import { useEffect, useRef, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import {
  lifecycle as appLifecycle,
  type ConnectionLifecycle,
  type OverlayState,
} from "../game/lifecycle";
import { fa } from "../i18n/fa";
import { useSession } from "../store/context";
import { Button } from "./Button";

/** A recovered socket shouldn't rip the overlay away instantly — a sub-second
 *  flash reads as broken. Hold the reconnecting spinner for at least this long so
 *  it lands as ONE smooth, deliberate beat, then drop the player back in. */
const RECONNECT_MIN_VISIBLE_MS = 1000;

/**
 * App-level connection overlay — mounted once at the root (main.tsx, next to
 * InvitePrompt) so it covers any live table. Driven by the lifecycle store:
 *
 *  - **reconnecting** — a smooth spinner + one Persian line while the backoff
 *    cycle silently re-establishes the socket after a screen-lock/backgrounding
 *    kill (no attempt counter — the retry count is an implementation detail, and
 *    the backoff already keeps it from hammering the server);
 *  - **failed** — the cycle gave up (or an uncaught error was routed here):
 *    manual retry + exit;
 *  - **seatExpired** — the online seat's reconnection window lapsed and a bot
 *    holds the seat now: back to the lobby.
 *
 * EXIT ALWAYS WORKS: every button here only flips local state (dismiss +
 * setView) — synchronously. Network cleanup rides the game container's
 * unmount, whose leave is fire-and-forget by contract (never awaited).
 */
export function ConnectionOverlay({
  lifecycle = appLifecycle,
}: {
  lifecycle?: ConnectionLifecycle;
}): ReactNode {
  const storeOverlay = useStore(lifecycle.store, (s) => s.overlay);
  const view = useSession((s) => s.view);
  const setView = useSession((s) => s.setView);
  const leaveTable = useSession((s) => s.leaveTable);
  // card-a5 item 1 — «خروج» from a PUBLIC table in progress is asked by the table's own confirmation
  // (`setView` parks it as `pendingExit`); that dialog sits at the same z-index as this overlay, so the
  // overlay steps aside while it is up. The reconnect cycle carries on underneath.
  const exitPending = useSession((s) => s.pendingExit !== null);

  // Minimum-visible smoothing: `displayed` mirrors the lifecycle overlay, EXCEPT
  // a reconnecting→none transition (a fast reconnect) is held for the min beat so
  // the animation doesn't flash. Terminal states (failed/seatExpired) show at
  // once, and leaving the game view (the exit button) hides via the guard below.
  const [displayed, setDisplayed] = useState<OverlayState>(storeOverlay);
  const holdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const clearHold = (): void => {
      if (holdRef.current) {
        clearTimeout(holdRef.current);
        holdRef.current = null;
      }
    };
    if (storeOverlay.kind === "reconnecting") {
      setDisplayed(storeOverlay);
      clearHold();
      holdRef.current = setTimeout(() => {
        holdRef.current = null;
        // Back already? drop the spinner now the min beat has elapsed.
        if (lifecycle.store.getState().overlay.kind === "none") setDisplayed({ kind: "none" });
      }, RECONNECT_MIN_VISIBLE_MS);
      return;
    }
    if (storeOverlay.kind === "none") {
      if (holdRef.current) return; // still inside the min beat — keep the spinner up
      setDisplayed({ kind: "none" });
      return;
    }
    clearHold(); // failed / seatExpired — terminal, no smoothing
    setDisplayed(storeOverlay);
  }, [storeOverlay, lifecycle]);
  useEffect(
    () => () => {
      if (holdRef.current) clearTimeout(holdRef.current);
    },
    [],
  );

  // Connection recovery is a table-side concern; outside the game view the
  // lifecycle still reconnects silently but never covers the screen.
  if (displayed.kind === "none" || view !== "game" || exitPending) return null;

  /*
   * «خروج» WHILE RECONNECTING IS AN ACT — and at a PUBLIC table in progress it is «خروج» (card-a5
   * item 1): `setView` parks it and the table shows «خروج»'s own confirmation; confirmed, the leave waits
   * for this very reconnect to bring the socket back and is charged exactly as «خروج»; never back, it
   * is free. Everywhere else it navigates NOW, as it always did (the container unmount detaches the
   * socket), and the overlay is dismissed only once it has.
   */
  const exit = (): void => {
    setView("lobby");
  };
  /*
   * …but out of a FAILED reconnect, or a seat the server already let go, there is nothing to charge:
   * «a failed reconnect» and «the seat already gone» stay FREE (card-a5 item 1). These leave without the
   * public-table guard.
   */
  const exitFree = (): void => {
    lifecycle.dismiss();
    leaveTable("lobby"); // navigate NOW; the container unmount detaches the socket
  };

  // PRE-START expiry has no bot takeover — the copy must say "seat freed",
  // never "a bot replaced you" (the game had not even started).
  const preStart = displayed.kind === "seatExpired" && displayed.preStart === true;
  const heading =
    displayed.kind === "reconnecting"
      ? fa.connection.reconnecting
      : displayed.kind === "failed"
        ? fa.connection.failedTitle
        : preStart
          ? fa.connection.seatFreedTitle
          : fa.connection.seatExpiredTitle;

  return (
    <div
      role="alertdialog"
      aria-label={heading}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70, // above every table overlay (paused=40, timers=45, toasts=60)
        background: "rgba(0,0,0,.6)",
        display: "grid",
        placeItems: "center",
        padding: 22,
      }}
    >
      <div
        dir="rtl"
        style={{
          background: "var(--surface)",
          borderRadius: "var(--radius-lg)",
          padding: "26px 22px",
          width: "100%",
          maxWidth: 320,
          textAlign: "center",
          border: "1px solid var(--hairline)",
          boxShadow: "0 24px 60px rgba(0,0,0,.5)",
          animation: "bg-pop 0.4s ease",
          display: "grid",
          gap: 10,
          justifyItems: "center",
        }}
      >
        {displayed.kind === "reconnecting" ? (
          <>
            <div
              aria-hidden
              style={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                marginBottom: 4,
                border: "3px solid var(--hairline)",
                borderTopColor: "var(--accent)",
                animation: "bg-spin 1s linear infinite",
              }}
            />
            <p style={{ margin: 0, fontWeight: 800, fontSize: 17 }}>{fa.connection.reconnecting}</p>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              {fa.connection.reconnectingBody}
            </p>
            <Button variant="ghost" onClick={exit} style={{ width: "100%", marginTop: 6 }}>
              {fa.connection.exit}
            </Button>
          </>
        ) : displayed.kind === "failed" ? (
          <>
            <div aria-hidden style={{ fontSize: 40 }}>
              📡
            </div>
            <p style={{ margin: 0, fontWeight: 800, fontSize: 17 }}>{fa.connection.failedTitle}</p>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              {fa.connection.failedBody}
            </p>
            <Button
              variant="primary"
              onClick={() => lifecycle.retry()}
              style={{ width: "100%", marginTop: 6 }}
            >
              {fa.connection.retry}
            </Button>
            <Button variant="ghost" onClick={exitFree} style={{ width: "100%" }}>
              {fa.connection.exit}
            </Button>
          </>
        ) : (
          <>
            <div aria-hidden style={{ fontSize: 40 }}>
              {preStart ? "⏳" : "🤖"}
            </div>
            <p style={{ margin: 0, fontWeight: 800, fontSize: 17 }}>{heading}</p>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>
              {preStart ? fa.connection.seatFreedBody : fa.connection.seatExpiredBody}
            </p>
            <Button variant="primary" onClick={exitFree} style={{ width: "100%", marginTop: 6 }}>
              {fa.connection.backToLobby}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
